import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { motion } from "framer-motion";
import PageHeader from "./PageHeader";

const UIPage = () => {
  useEffect(() => {
    // Update document title and meta description for SEO
    document.title = "ChatGPT UI Enhancement | Improve Your Workflow";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'ChatGPT Power-Up improves your UI with folders, prompt tools, and more – without changing how ChatGPT works.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader />
      
      <div className="max-w-4xl mx-auto px-12">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Upgrade Your ChatGPT UI – Cleaner, Faster, Better
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Transform your ChatGPT experience with a cleaner, more organized interface. Add folders, prompt tools, and productivity features while keeping ChatGPT's familiar look and feel.
          </p>
        </motion.div>

        {/* UI Comparison */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Default ChatGPT UI vs. Power-Up Enhanced UI</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-4">Default ChatGPT Interface</h3>
              <div className="bg-gray-100 p-6 rounded-lg mb-4">
                <div className="space-y-3 text-left">
                  <div className="bg-white p-3 rounded border">
                    <div className="text-sm text-gray-500">Chat History</div>
                    <div className="text-xs text-gray-400">• Long list of conversations</div>
                    <div className="text-xs text-gray-400">• No organization</div>
                    <div className="text-xs text-gray-400">• Hard to find specific chats</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="text-sm text-gray-500">Prompt Input</div>
                    <div className="text-xs text-gray-400">• Basic text input</div>
                    <div className="text-xs text-gray-400">• No saved prompts</div>
                    <div className="text-xs text-gray-400">• Manual retyping</div>
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-sm">Limited functionality, basic organization</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-4">Power-Up Enhanced Interface</h3>
              <div className="bg-blue-50 p-6 rounded-lg mb-4">
                <div className="space-y-3 text-left">
                  <div className="bg-white p-3 rounded border border-blue-200">
                    <div className="text-sm text-blue-600 font-medium">Organized Chat History</div>
                    <div className="text-xs text-green-600">✓ Folders for organization</div>
                    <div className="text-xs text-green-600">✓ Easy chat management</div>
                    <div className="text-xs text-green-600">✓ Quick access to conversations</div>
                  </div>
                  <div className="bg-white p-3 rounded border border-blue-200">
                    <div className="text-sm text-blue-600 font-medium">Enhanced Prompt Tools</div>
                    <div className="text-xs text-green-600">✓ Saved prompt templates</div>
                    <div className="text-xs text-green-600">✓ One-click insertion</div>
                    <div className="text-xs text-green-600">✓ Keyboard shortcuts</div>
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-sm">Enhanced functionality, better organization</p>
            </div>
          </div>
        </motion.div>

        {/* Key UI Improvements */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Key UI Improvements</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">📁</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-3">Organized Sidebar</h3>
              <p className="text-gray-600 text-sm">Clean folder structure replaces the chaotic chat list. Find conversations instantly with logical organization.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-3">Quick Actions</h3>
              <p className="text-gray-600 text-sm">Context menus and keyboard shortcuts for common actions. Work faster with less clicking.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">💾</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-3">Prompt Library</h3>
              <p className="text-gray-600 text-sm">Dedicated space for your saved prompts. Access templates with one click, no more hunting through notes.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-3">Bulk Operations</h3>
              <p className="text-gray-600 text-sm">Select multiple chats for deletion, moving, or other operations. Manage your conversations efficiently.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">🔍</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-3">Enhanced Search</h3>
              <p className="text-gray-600 text-sm">Better search functionality with filters and categories. Find what you need faster.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">⚙️</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-3">Customizable Layout</h3>
              <p className="text-gray-600 text-sm">Adjust the interface to match your workflow. Collapse sections, resize panels, and personalize your experience.</p>
            </div>
          </div>
        </motion.div>

        {/* Ease of Use */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8 mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Intuitive Design, Zero Learning Curve</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">🎨 Familiar Interface</h3>
              <p className="text-gray-700 mb-4">We've enhanced ChatGPT's UI without changing its core design. Everything feels natural and intuitive.</p>
              <ul className="space-y-2 text-gray-600">
                <li>• Same ChatGPT layout you know</li>
                <li>• Seamless integration</li>
                <li>• No disruption to your workflow</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">🚀 Instant Productivity</h3>
              <p className="text-gray-700 mb-4">Start using enhanced features immediately. No training required, no complex setup needed.</p>
              <ul className="space-y-2 text-gray-600">
                <li>• Works right after installation</li>
                <li>• Discover features naturally</li>
                <li>• Optional advanced features</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Productivity Boost */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">How UI Improvements Boost Your Productivity</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">⏱️</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Save Time</h3>
              <p className="text-gray-600">Find conversations faster, insert prompts instantly, and manage chats efficiently. What used to take minutes now takes seconds.</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">🧠</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Reduce Cognitive Load</h3>
              <p className="text-gray-600">Organized interface means less mental effort finding what you need. Focus on your work, not on navigating the UI.</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">📈</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Improve Quality</h3>
              <p className="text-gray-600">Better organization leads to better context awareness and more consistent results across your conversations.</p>
            </div>
          </div>
        </motion.div>

        {/* Related Features */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Explore the Complete UI Enhancement Suite</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/chatgpt-folders" className="block">
              <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition-all">
                <h3 className="font-semibold text-gray-900 mb-2">ChatGPT Folders</h3>
                <p className="text-gray-600 text-sm">Organize your conversations into folders for better workflow management and quick access.</p>
              </div>
            </Link>
            <Link to="/chatgpt-prompt-templates" className="block">
              <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition-all">
                <h3 className="font-semibold text-gray-900 mb-2">ChatGPT Prompt Templates</h3>
                <p className="text-gray-600 text-sm">Save and reuse your best prompts with our powerful template system.</p>
              </div>
            </Link>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white"
        >
          <h2 className="text-3xl font-bold mb-4">Make ChatGPT Work the Way You Want</h2>
          <p className="text-xl mb-8 opacity-90">
            Transform your ChatGPT experience with a cleaner, more organized interface
          </p>
          <a
            href="https://chromewebstore.google.com/detail/chatgpt-power-up/ooleaojggfoigcdkodigbcjnabidihgi"
            className="inline-block"
          >
            <Button className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
              Upgrade Your ChatGPT UI – Add to Chrome
            </Button>
          </a>
          <p className="text-sm opacity-75 mt-4">Free • No account required • Works instantly</p>
        </motion.div>
      </div>
    </div>
  );
};

export default UIPage; 